import { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppShell } from "@/components/AppShell";
import { PageTitle } from "@/components/PageTitle";
import { LoadingBlock } from "@/components/States";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { EditorStatusProvider } from "@/context/EditorStatusContext";
import { safeNext } from "@/lib/nav";
import "@/App.css";

import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import NotInvited from "@/pages/NotInvited";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TenantOverview = lazy(() => import("@/pages/TenantOverview"));
const Editor = lazy(() => import("@/pages/Editor"));
const Review = lazy(() => import("@/pages/Review"));
const Preview = lazy(() => import("@/pages/Preview"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const ActivityPage = lazy(() => import("@/pages/ActivityPage"));
const SyncPage = lazy(() => import("@/pages/SyncPage"));
const HandoverPage = lazy(() => import("@/pages/HandoverPage"));
const TenantSettings = lazy(() => import("@/pages/TenantSettings"));
const AdminInvites = lazy(() => import("@/pages/AdminInvites"));

const FullPageLoading = () => (
  <div className="mx-auto max-w-3xl px-4 py-16">
    <LoadingBlock rows={2} />
  </div>
);

const Protected = ({ children, agencyOnly = false }) => {
  const { status, isAgency } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageLoading />;
  if (status === "forbidden")
    return <Navigate to="/not-invited?reason=revoked" replace />;
  if (status !== "authenticated") {
    // Read from the router, not window.location: after signing out the browser URL can already
    // have moved on, which used to produce ?next=/login and bounce the sign-in screen forever.
    const next = safeNext(location.pathname + location.search);
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (agencyOnly && !isAgency) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
};

const HomeRedirect = () => {
  const { isAgency, tenants } = useAuth();
  if (isAgency) return <Dashboard />;
  if (tenants.length === 1)
    return <Navigate to={`/t/${tenants[0].slug}`} replace />;
  if (tenants.length > 1) return <Dashboard />;
  return <Navigate to="/not-invited?reason=no_client" replace />;
};

const EditorRoute = () => {
  const { slug } = useParams();
  return <Editor key={slug} />;
};

function AppRoutes() {
  const location = useLocation();

  // Checked during render, not in an effect: an effect runs after the first paint, by which
  // point a protected route would already have bounced the arriving user to /login. The hash is
  // read from useLocation so React re-renders once the callback navigates away.
  if (String(location.hash).includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<FullPageLoading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/not-invited" element={<NotInvited />} />

        <Route
          path="/"
          element={
            <Protected>
              <HomeRedirect />
            </Protected>
          }
        />
        <Route
          path="/t/:slug"
          element={
            <Protected>
              <TenantOverview />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/edit"
          element={
            <Protected>
              <EditorRoute />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/edit/:section"
          element={
            <Protected>
              <EditorRoute />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/edit/:section/:item"
          element={
            <Protected>
              <EditorRoute />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/review"
          element={
            <Protected>
              <Review />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/preview"
          element={
            <Protected>
              <Preview />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/history"
          element={
            <Protected>
              <HistoryPage />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/activity"
          element={
            <Protected>
              <ActivityPage />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/sync"
          element={
            <Protected agencyOnly>
              <SyncPage />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/handover"
          element={
            <Protected agencyOnly>
              <HandoverPage />
            </Protected>
          }
        />
        <Route
          path="/t/:slug/settings"
          element={
            <Protected agencyOnly>
              <TenantSettings />
            </Protected>
          }
        />
        <Route
          path="/admin/invites"
          element={
            <Protected agencyOnly>
              <AdminInvites />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EditorStatusProvider>
          <PageTitle />
          <AppRoutes />
          <Toaster position="bottom-right" />
        </EditorStatusProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
